'use client'

import React from 'react'
import Link from 'next/link'
import { Flex, Text, UnstyledButton } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'
import { GenAiIcon } from '@/components/Icons/GenAiIcon'

const CHEVRON_GRAY = 'var(--mantine-color-gray-5)'

interface MenuRowProps {
    name: string
    onClick?: (e: React.MouseEvent) => void
    href?: string
    hasChevron?: boolean
    showGenAiIcon?: boolean
}

export function MenuRow({ name, onClick, href, hasChevron, showGenAiIcon }: MenuRowProps) {
    const content = (
        <Flex
            justify="space-between"
            align="center"
            onMouseEnter={applyHoverStyles(true)}
            onMouseLeave={applyHoverStyles(false)}
            style={{ cursor: 'pointer' }}
        >
            <Flex align="center" gap={8}>
                <Text
                    data-menu-text
                    component="span"
                    fz={24}
                    fw={450}
                    lh="130%"
                    lts="-0.24px"
                    c="var(--gm-fg-primary-black, #1f1e1d)"
                    display="block"
                    style={{ transition: 'color 0.2s ease' }}
                >
                    {name}
                </Text>
                {showGenAiIcon && <GenAiIcon size={20} strokeColor="var(--gm-blue, #2d1aff)" />}
                {!hasChevron && (
                    <IconArrowRight
                        data-menu-arrow
                        size={20}
                        color="var(--gm-blue, #2d1aff)"
                        style={{
                            opacity: 0,
                            transform: 'translateX(-4px)',
                            transition: 'opacity 0.2s ease, transform 0.2s ease',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Flex>
            {hasChevron && (
                <IconArrowRight
                    data-menu-chevron
                    size={20}
                    color={CHEVRON_GRAY}
                    stroke={1.5}
                    style={{ transition: 'color 0.2s ease' }}
                />
            )}
        </Flex>
    )

    if (href) {
        return (
            <UnstyledButton
                component={Link}
                href={href}
                onClick={onClick as any}
                style={{ display: 'block', width: '100%' }}
            >
                {content}
            </UnstyledButton>
        )
    }
    return (
        <UnstyledButton onClick={onClick as any} style={{ display: 'block', width: '100%' }}>
            {content}
        </UnstyledButton>
    )
}

// Mantine doesn't expose a hover pseudo for our exact effect, and we want the
// arrow + chevron + text to animate in concert, so we drive style via DOM refs.
function applyHoverStyles(hovering: boolean) {
    const HOVER = 'var(--gm-blue, #2d1aff)'
    const REST_TEXT = 'var(--gm-fg-primary-black, #1f1e1d)'
    return (e: React.MouseEvent<HTMLElement>) => {
        const text = e.currentTarget.querySelector('[data-menu-text]') as HTMLElement | null
        const arrow = e.currentTarget.querySelector('[data-menu-arrow]') as HTMLElement | null
        const chevron = e.currentTarget.querySelector('[data-menu-chevron]') as HTMLElement | null
        if (text) text.style.color = hovering ? HOVER : REST_TEXT
        if (arrow) {
            arrow.style.opacity = hovering ? '1' : '0'
            arrow.style.transform = hovering ? 'translateX(0)' : 'translateX(-4px)'
        }
        if (chevron) chevron.style.color = hovering ? HOVER : CHEVRON_GRAY
    }
}
